import { AnyAction, createSlice, Slice } from '@reduxjs/toolkit';
import type { ICurrentProject, INormalizedLandingElement } from '~/src/services/projects';
import type { TAppDispatch, TState } from '~/src/store';
import { startLoading, stopLoading } from '~/src/store/slices/appSlice';
import { getProject, updateProject, publishProject, unpublishProject } from '~/src/services/projects/projectApi';
import { addToStore, removeFromStore } from '~/src/services/blocks/updateStore';
import { EElementType } from '~/src/services/projects';

export interface ISelected {
    id?: string;
    _id?: string;
    type: EElementType;
}

interface ICurrentProjectSlice {
    project?: ICurrentProject;
    selectedItem?: ISelected;
    isError: boolean;
}

const currentProjectSlice: Slice<ICurrentProjectSlice> = createSlice({
    name: 'currentProject',
    initialState: {
        project: undefined,
        selectedItem: undefined,
        isError: false,
    },
    reducers: {
        update: (state: ICurrentProjectSlice, action: AnyAction) => {
            state.project = action.payload;
        },
        select: (state: ICurrentProjectSlice, action: AnyAction) => {
            const { id, type, _id } = action.payload;
            state.selectedItem = {
                id,
                _id,
                type,
            };
        },
        toggle: (state: ICurrentProjectSlice, action: AnyAction) => {
            state.project.body = action.payload;
        },
        error: (state: ICurrentProjectSlice, action: AnyAction) => {
            state.isError = !!action.payload;
        },
        clear: (state: ICurrentProjectSlice) => {
            state.project = undefined;
        },
    },
    extraReducers: builder => {
        builder.addCase('properties/change', (state, action: AnyAction) => {
            const { project } = action.payload;
            state.project = project;
        });
    },
});

const { actions } = currentProjectSlice;

export const { select } = currentProjectSlice.actions;

export const get = (id: string) => {
    return async (dispatch: TAppDispatch, getState: () => TState): Promise<void> => {
        dispatch(startLoading());
        const { auth: { tokenInfo: { token } } } = getState();

        const resp = await getProject(token, id);
        if (resp.isSuccess === true) {
            dispatch(actions.update(resp.payload));
        } else {
            dispatch(actions.error(resp.error));
        }

        dispatch(stopLoading());
    };
};

export const update = (id: string, project: ICurrentProject) => {
    return async (dispatch: TAppDispatch, getState: () => TState): Promise<void> => {
        dispatch(startLoading());
        const { auth: { tokenInfo: { token } } } = getState();

        const resp = await updateProject(token, id, project);
        if (resp.isSuccess === true) {
            dispatch(actions.update(resp.payload));
        } else {
            dispatch(actions.error(resp.error));
        }

        dispatch(stopLoading());
    };
};

export const saveAndPreview = (id: string, project: ICurrentProject) => {
    return async (dispatch: TAppDispatch): Promise<void> => {
        await dispatch(update(id, project));
        dispatch(startLoading());

        const url = window.location.host;

        if (window) {
            window.open(`//${url}/landing/${project.url}/preview`, '_blank');
        }
        dispatch(stopLoading());
    };
};

export const publish = (project: ICurrentProject) => {
    return async (dispatch: TAppDispatch, getState: () => TState): Promise<void> => {
        const { auth: { tokenInfo: { token } } } = getState();

        dispatch(startLoading());
        const resp = await publishProject(token, project._id);
        if (resp.isSuccess === true) {
            dispatch(actions.update({ ...project, publicVersionId: resp.payload.publicVersionId, publishedAt: resp.payload.publishedAt }));
        } else {
            dispatch(actions.error(resp.error));
        }

        dispatch(stopLoading());
    };
};

export const unpublish = (project: ICurrentProject) => {
    return async (dispatch: TAppDispatch, getState: () => TState): Promise<void> => {
        const { auth: { tokenInfo: { token } } } = getState();

        dispatch(startLoading());
        const resp = await unpublishProject(token, project._id);
        if (resp.isSuccess === true) {
            dispatch(actions.update({ ...project, publicVersionId: null, publishedAt: null }));
        } else {
            dispatch(actions.error(resp.error));
        }

        dispatch(stopLoading());
    };
};

export const clearCurrentProject = () => {
    return (dispatch: TAppDispatch): void => {
        dispatch(actions.clear({}));
    };
};

export const toggle = (elements: INormalizedLandingElement) => {
    return (dispatch: TAppDispatch, getState: () => TState): void => {
        const { currentProject: { project: { body } } } = getState();
        dispatch(actions.toggle({
            ...body,
            ...elements,
        }));
    };
};

export const addBlock = (blockTitle: string) => {
    return (dispatch: TAppDispatch, getState: () => TState): void => {
        const { currentProject: { project } } = getState();
        const res = addToStore(project, blockTitle);
        dispatch(actions.update(res));
    };
};

export const removeBlock = (id: string) => {
    return (dispatch: TAppDispatch, getState: () => TState): void => {
        const { currentProject: { project } } = getState();
        dispatch(actions.update(removeFromStore(project, id)));
    };
};

export const removeElement = (id: string) => {
    return (dispatch: TAppDispatch, getState: () => TState): void => {
        const { currentProject: { project } } = getState();
        dispatch(actions.update(removeFromStore(project, id)));
    };
};

export const selectCurrentProject = (state: TState): ICurrentProjectSlice => state.currentProject;

export default currentProjectSlice.reducer;
